import { ConsentStatusPill } from '@/components/session/ConsentStatusPill';
import { getConsentPillConfig } from '@/components/session/ConsentStatusPill.config';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';
import type { AttestDecision, ConsentAttestControlProps } from './ConsentAttestControl.types';
import { ATTEST_COPY, useConsentAttest } from './useConsentAttest';

/**
 * The consent pill as a control: opens the status with "Staðfesta samþykki" /
 * "Skrá synjun", confirms, then records the clinician's attestation through
 * `onAttest`. Shared by the main app's patient picker and the embed.
 *
 * Clicks are stopped at this control's root: it sits inside the patient
 * picker's trigger button, and the popover and dialog portals (backdrop
 * included) still bubble React events up to it.
 */
export const ConsentAttestControl = ({
  status,
  onAttest,
  isAttesting,
  showLabel = false,
}: ConsentAttestControlProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { decision, setDecision, confirm } = useConsentAttest({ onAttest, isAttesting });

  const config = getConsentPillConfig(status);
  if (!config) return null;
  const Icon = config.icon;

  const openDialog = (next: AttestDecision) => () => {
    setPopoverOpen(false);
    setDecision(next);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: only stops bubbling; the controls inside handle keys
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={config.label}
            className={cn(
              'group flex shrink-0 cursor-pointer items-center justify-center gap-1.5 transition-all duration-150 ease-out',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
            )}
          >
            <ConsentStatusPill status={status} interactive showLabel={showLabel} />
          </button>
        </PopoverTrigger>
        <PopoverContent side="bottom" align="end" className="w-72">
          <div className="flex items-start gap-2.5">
            <span className={cn('mt-0.5 shrink-0', config.iconClass)}>
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm leading-tight">{config.label}</p>
              <p className="mt-0.5 text-muted-foreground text-xs">{config.description}</p>
            </div>
          </div>
          <div className="mt-3 border-t pt-3">
            <p className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
              Skrá skriflegt samþykki
            </p>
            <div className="flex flex-col gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={isAttesting || status === 'active'}
                onClick={openDialog('permit')}
                className={cn(
                  'group justify-start transition-all duration-150',
                  'hover:-translate-y-px hover:border-green-500 hover:bg-green-50 hover:text-green-700 hover:shadow-sm',
                  'active:translate-y-0 active:shadow-none',
                  'dark:hover:bg-green-950/40 dark:hover:text-green-400'
                )}
              >
                <CheckCircle className="text-green-600 transition-transform duration-150 group-hover:scale-110" />
                <span>Staðfesta samþykki</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={isAttesting || status === 'rejected'}
                onClick={openDialog('deny')}
                className={cn(
                  'group justify-start transition-all duration-150',
                  'hover:-translate-y-px hover:border-red-500 hover:bg-red-50 hover:text-red-700 hover:shadow-sm',
                  'active:translate-y-0 active:shadow-none',
                  'dark:hover:bg-red-950/40 dark:hover:text-red-400'
                )}
              >
                <XCircle className="text-red-600 transition-transform duration-150 group-hover:scale-110" />
                <span>Skrá synjun</span>
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={decision !== null} onOpenChange={(o) => !isAttesting && !o && setDecision(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{ATTEST_COPY[decision ?? 'permit'].title}</AlertDialogTitle>
            <AlertDialogDescription>{ATTEST_COPY[decision ?? 'permit'].description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAttesting}>Hætta við</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} disabled={isAttesting}>
              {isAttesting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Skrái...
                </>
              ) : (
                ATTEST_COPY[decision ?? 'permit'].action
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
};
